import {useCallback, useEffect, useRef} from "react";

import type {ILatLonCoordinate} from "@reva/shared/geo-reference";
import {roundToDecimals} from "@reva/shared/math";
import type OlMap from "ol/Map";
import {fromLonLat, toLonLat} from "ol/proj";

import {selectPersistenceInitialized} from "../redux/persistenceSlice";
import {type MapSettings, persistMapSettings} from "../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector, useRevaStore} from "../redux/revaHooks";

const EPSG_3857 = "EPSG:3857";
const DEBOUNCE_MS = 500;
/** Auflösung (m/px in EPSG:3857) bei Zoom 0 mit 256-px-Kacheln. */
const RESOLUTION_ZOOM_0 = (2 * Math.PI * 6378137) / 256;
/** Mindest-Halbbreite/-höhe der Karte in Pixeln, mit der die Sichtbarkeit des Ankers geprüft wird. */
const ANCHOR_MIN_HALF_SIZE_PX = 200;

/** Gespeicherte Startansicht und der moveend-Callback, der jede Bewegung entprellt persistiert. */
export interface UseMapSettingsResult {
    /** Gespeicherte Ansicht, oder null solange nichts gelesen wurde bzw. die Ansicht verworfen wurde. */
    initialView: MapSettings | null;
    /** An das moveend-Event der Karte zu hängen. */
    onMapMoveEnd: (map: OlMap | null) => void;
}

/** Auf 6 Nachkommastellen runden. */
const round6 = (value: number) => roundToDecimals(value, 6);

/** Parse a stored map settings value (object or JSON string) into a view object. */
function parseStoredValue(raw: unknown): MapSettings | null {
    if (!raw) return null;
    try {
        const {zoom, lat, lon} = (typeof raw === "string" ? JSON.parse(raw) : raw) as Partial<MapSettings>;
        if (typeof zoom === "number" && typeof lat === "number" && typeof lon === "number") {
            return {zoom, lat, lon};
        }
    } catch {
        // ignore malformed JSON
    }
    return null;
}

/**
 * Prüft, ob ein Punkt in einer gespeicherten Ansicht sichtbar wäre. Die Kartengröße ist beim Lesen
 * noch unbekannt, daher wird konservativ mit {@link ANCHOR_MIN_HALF_SIZE_PX} gerechnet.
 * @param view gespeicherte Ansicht
 * @param anchor Punkt, der sichtbar sein muss (z.B. die Adresse)
 */
export function isAnchorVisibleInView(view: MapSettings, anchor: ILatLonCoordinate): boolean {
    const [cx, cy] = fromLonLat([view.lon, view.lat], EPSG_3857);
    const [ax, ay] = fromLonLat([anchor.lon, anchor.lat], EPSG_3857);
    const halfSize = (RESOLUTION_ZOOM_0 / 2 ** view.zoom) * ANCHOR_MIN_HALF_SIZE_PX;
    return Math.abs(ax - cx) <= halfSize && Math.abs(ay - cy) <= halfSize;
}

/**
 * Reads saved settings from documentProperties in Redux once persistence is
 * initialized, and provides a moveend callback that debounces and persists the
 * current map view to the backend.
 *
 * @param mapKey the documentProperties key (e.g. "overview_map_settings")
 * @param anchor Punkt, der in der gespeicherten Ansicht sichtbar sein muss, sonst wird sie verworfen
 *        (#19447: Altbestände, die unter dem falschen Thema gespeichert wurden). null = Anker noch
 *        nicht geladen, mit dem Lesen warten; ohne Angabe keine Prüfung.
 */
export function useMapSettings(mapKey: string, anchor?: ILatLonCoordinate | null): UseMapSettingsResult {
    const dispatch = useRevaDispatch();
    const store = useRevaStore();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingSettingsRef = useRef<MapSettings | null>(null);
    const initialMoveSkippedRef = useRef(false);
    // Zuletzt bekannte Ansicht. OpenLayers feuert moveend auch bei reinen Größenänderungen der Karte
    // (Layout-Sprung, Fenster-Resize, Seitenleiste), Center und Zoom bleiben dabei gleich (#19553).
    const lastViewRef = useRef<MapSettings | null>(null);
    const hasReadPersistence = useRef(false);

    // Subscribe to persistence initialization so React re-renders this component
    // when persistence becomes available. Without this, the map may be created
    // before the stored value exists and never get the correct initial position.
    const initialized = useRevaSelector(selectPersistenceInitialized);

    // Single ref that always holds the most recent known position. Initialized
    // from persistence on first read, then updated on every moveend. This avoids
    // subscribing to the stored value itself (which would cause re-renders on
    // every persist cycle).
    const viewRef = useRef<MapSettings | null>(null);
    if (!hasReadPersistence.current && initialized && anchor !== null) {
        const properties = store.getState().objectParameters.properties;
        const stored = parseStoredValue(properties[mapKey]);
        viewRef.current = stored && anchor && !isAnchorVisibleInView(stored, anchor) ? null : stored;
        hasReadPersistence.current = true;
    }

    // Flush pending persist on unmount so map position is not lost when navigating away
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            if (pendingSettingsRef.current) {
                void dispatch(persistMapSettings(mapKey, pendingSettingsRef.current));
                pendingSettingsRef.current = null;
            }
        };
    }, [dispatch, mapKey]);

    const onMapMoveEnd = useCallback(
        (map: OlMap | null) => {
            if (!map) return;

            const view = map.getView();
            const zoom = view.getZoom();
            const center = view.getCenter();
            if (zoom == null || !center) return;

            const [lon, lat] = toLonLat(center, EPSG_3857);

            const settings: MapSettings = {
                zoom: round6(zoom),
                lat: round6(lat),
                lon: round6(lon),
            };

            const lastView = lastViewRef.current;
            lastViewRef.current = settings;

            // Das erste moveend entsteht bei der Kartenerzeugung und liefert nur die Ausgangsansicht.
            if (!initialMoveSkippedRef.current) {
                initialMoveSkippedRef.current = true;
                return;
            }

            // Ansicht unverändert: keine Nutzerbewegung, nichts speichern.
            if (
                lastView &&
                lastView.zoom === settings.zoom &&
                lastView.lat === settings.lat &&
                lastView.lon === settings.lon
            ) {
                return;
            }

            viewRef.current = settings;

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            pendingSettingsRef.current = settings;
            debounceTimerRef.current = setTimeout(() => {
                pendingSettingsRef.current = null;
                void dispatch(persistMapSettings(mapKey, settings));
            }, DEBOUNCE_MS);
        },
        [dispatch, mapKey],
    );

    // Before persistence is loaded, return null so the map uses its defaults.
    const initialView = hasReadPersistence.current ? viewRef.current : null;
    return {initialView, onMapMoveEnd};
}
